import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="time-calculator"
// Wraps the entire task group form and provides live time calculation (forwards or backwards).
export default class extends Controller {
  static targets = [
    "deadlineInput", "deadlineBadge",
    "summaryStart", "summaryTotal", "summaryCount",
    "anchorLabel", "summaryStartLabel",
    "forwardField", "directionBtn"
  ]
  static values = {
    locale:               { type: String,  default: "en" },
    forward:              { type: Boolean, default: false },
    backwardLabel:        { type: String,  default: "Deadline" },
    forwardLabel:         { type: String,  default: "Starting time" },
    backwardSummaryLabel: { type: String,  default: "Start time" },
    forwardSummaryLabel:  { type: String,  default: "Finish time" }
  }

  connect() {
    this.calculate()
    this._updateDirectionUI()
  }

  // Called when the direction toggle buttons are clicked
  toggleDirection(event) {
    this.forwardValue = event.currentTarget.dataset.direction === "forward"
    this._updateDirectionUI()
    this.calculate()
  }

  calculate() {
    const val = this.deadlineInputTarget.value
    if (!val) { this.clearDisplays(); return }

    const [datePart, timePart] = val.split("T")
    if (!timePart) { this.clearDisplays(); return }
    const [yr, mo, dy] = datePart.split("-").map(Number)
    const [hr, mn]     = timePart.split(":").map(Number)
    const anchor = new Date(yr, mo - 1, dy, hr, mn)
    if (isNaN(anchor)) { this.clearDisplays(); return }

    if (this.hasDeadlineBadgeTarget) {
      this.deadlineBadgeTarget.textContent = this.fmtFull(anchor)
    }

    // Collect visible, non-destroyed task rows in DOM order
    const tasks = Array.from(this.element.querySelectorAll(".nested-task")).filter(t => {
      if (t.style.display === "none") return false
      const destroy = t.querySelector("input[name*='_destroy']")
      return !(destroy && destroy.value === "1")
    })

    // Update position badges
    tasks.forEach((task, i) => {
      const badge = task.querySelector("[data-position-badge]")
      if (badge) badge.textContent = i + 1
    })

    const intervals = new Array(tasks.length)

    if (this.forwardValue) {
      // Forward: anchor is the start, tasks chain forward
      let currentMs = anchor.getTime()
      for (let i = 0; i < tasks.length; i++) {
        const durationInput = tasks[i].querySelector("[data-duration-field]")
        const minsHidden    = tasks[i].querySelector("[data-minutes-field]")
        const mins = Math.max(0, this.parseDuration(durationInput?.value))
        if (minsHidden) minsHidden.value = mins
        intervals[i] = { start: new Date(currentMs), end: new Date(currentMs + mins * 60000) }
        currentMs += mins * 60000
      }
    } else {
      // Backward: anchor is the deadline, tasks chain backward
      let currentMs = anchor.getTime()
      for (let i = tasks.length - 1; i >= 0; i--) {
        const durationInput = tasks[i].querySelector("[data-duration-field]")
        const minsHidden    = tasks[i].querySelector("[data-minutes-field]")
        const mins = Math.max(0, this.parseDuration(durationInput?.value))
        if (minsHidden) minsHidden.value = mins
        intervals[i] = { end: new Date(currentMs), start: new Date(currentMs - mins * 60000) }
        currentMs -= mins * 60000
      }
    }

    // Update time display on each task card
    tasks.forEach((task, i) => {
      const startEl  = task.querySelector("[data-start-display]")
      const endEl    = task.querySelector("[data-end-display]")
      const dayBadge = task.querySelector("[data-day-badge]")
      if (startEl) startEl.textContent = this.fmt(intervals[i].start)
      if (endEl)   endEl.textContent   = this.fmt(intervals[i].end)

      if (dayBadge) {
        const s = intervals[i].start
        const e = intervals[i].end
        const differentDay = s.getDate() !== e.getDate() || s.getMonth() !== e.getMonth() || s.getFullYear() !== e.getFullYear()
        if (differentDay) {
          dayBadge.textContent = this.fmtDate(s)
          dayBadge.classList.remove("hidden")
        } else {
          dayBadge.textContent = ""
          dayBadge.classList.add("hidden")
        }
      }
    })

    // Update summary footer
    const totalMins = tasks.reduce((sum, task) => {
      const durationInput = task.querySelector("[data-duration-field]")
      return sum + Math.max(0, this.parseDuration(durationInput?.value))
    }, 0)

    if (this.hasSummaryStartTarget) {
      if (tasks.length > 0) {
        // Forward: show when everything finishes; Backward: show when to start
        const summaryTime = this.forwardValue ? intervals[tasks.length - 1].end : intervals[0].start
        this.summaryStartTarget.textContent = this.fmtFull(summaryTime)
      } else {
        this.summaryStartTarget.textContent = "—"
      }
    }
    if (this.hasSummaryTotalTarget) {
      const h = Math.floor(totalMins / 60)
      const m = totalMins % 60
      this.summaryTotalTarget.textContent =
        totalMins === 0 ? "—" : (h > 0 ? `${h}h ${m}m` : `${m}m`)
    }
    if (this.hasSummaryCountTarget) {
      this.summaryCountTarget.textContent = tasks.length
    }
    const headerCount = this.element.querySelector("[data-task-count-header]")
    if (headerCount) headerCount.textContent = tasks.length
  }

  _updateDirectionUI() {
    // Swap anchor card label
    if (this.hasAnchorLabelTarget) {
      this.anchorLabelTarget.textContent = this.forwardValue
        ? this.forwardLabelValue
        : this.backwardLabelValue
    }

    // Swap summary start label
    if (this.hasSummaryStartLabelTarget) {
      this.summaryStartLabelTarget.textContent = this.forwardValue
        ? this.forwardSummaryLabelValue
        : this.backwardSummaryLabelValue
    }

    // Highlight active direction button
    this.directionBtnTargets.forEach(btn => {
      const isActive = (btn.dataset.direction === "forward") === this.forwardValue
      btn.classList.toggle("bg-blue-600",        isActive)
      btn.classList.toggle("text-white",         isActive)
      btn.classList.toggle("bg-white",           !isActive)
      btn.classList.toggle("dark:bg-zinc-800",   !isActive)
      btn.classList.toggle("text-zinc-500",      !isActive)
      btn.classList.toggle("dark:text-zinc-400", !isActive)
    })

    // Sync hidden field so the form submits the correct value
    if (this.hasForwardFieldTarget) {
      this.forwardFieldTarget.value = this.forwardValue ? "1" : "0"
    }
  }

  parseDuration(str) {
    if (!str || !str.trim()) return 0
    str = str.trim()
    if (str.includes(":")) {
      const [h, m] = str.split(":").map(s => parseInt(s) || 0)
      return h * 60 + m
    }
    return parseInt(str) || 0
  }

  fmt(date) {
    let h = date.getHours()
    const m = String(date.getMinutes()).padStart(2, "0")
    const ampm = h >= 12 ? "pm" : "am"
    h = h % 12 || 12
    return `${h}:${m}${ampm}`
  }

  fmtDate(date) {
    const month = new Intl.DateTimeFormat(this.localeValue, { month: "short" }).format(date)
    return `${month} ${date.getDate()}`
  }

  fmtFull(date) {
    const day   = new Intl.DateTimeFormat(this.localeValue, { weekday: "short" }).format(date)
    const month = new Intl.DateTimeFormat(this.localeValue, { month: "short" }).format(date)
    return `${day} ${date.getDate()} ${month} · ${this.fmt(date)}`
  }

  clearDisplays() {
    if (this.hasDeadlineBadgeTarget)    this.deadlineBadgeTarget.textContent  = ""
    if (this.hasSummaryStartTarget)     this.summaryStartTarget.textContent   = "—"
    if (this.hasSummaryTotalTarget)     this.summaryTotalTarget.textContent   = "—"
    if (this.hasSummaryCountTarget)     this.summaryCountTarget.textContent   = "0"
    this.element.querySelectorAll("[data-start-display]").forEach(el => el.textContent = "—")
    this.element.querySelectorAll("[data-end-display]").forEach(el => el.textContent   = "—")
    this.element.querySelectorAll("[data-day-badge]").forEach(el => { el.textContent = ""; el.classList.add("hidden") })
    const headerCount = this.element.querySelector("[data-task-count-header]")
    if (headerCount) headerCount.textContent = "0"
  }
}
